import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { CtaBand, FaqBlock, Label, PageHero, SectionHead } from "@/components/Shared";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { IMG, SERVICE_INDEX } from "@/lib/site";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Leistungen", to: "/leistungen" },
];

const faq = [
  { q: "Welche Leistungen gehören zum Winterdienst?", a: "Schneeräumung, Streudienst und Eisbeseitigung auf Gehwegen, Zufahrten, Höfen, Treppen, Eingängen, Parkplätzen und Parkdecks – für private und gewerbliche Objekte." },
  { q: "Kann ich einzelne Leistungen buchen?", a: "Ja. Sie können den Umfang festlegen, zum Beispiel nur Gehweg und Eingang oder das komplette Außengelände." },
  { q: "Arbeiten Sie maschinell oder von Hand?", a: "Beides, je nach Fläche. Große Flächen maschinell, Treppen, Engstellen und empfindliche Beläge von Hand." },
  { q: "Was kosten die Leistungen?", a: "Preise nennen wir objektbezogen – Angebot auf Anfrage, abhängig von Fläche, Umfang und Lage." },
];

const Leistungen = () => (
  <main data-testid="page-leistungen">
    <Seo
      title="Leistungen Winterdienst Bremen | Schneeräumung, Streudienst, Eis"
      description="Alle Winterdienst-Leistungen von Bremer Schneemann: Schneeräumung, Streudienst, Eisbeseitigung, Gehwege, Zufahrten, Parkplätze, Parkhäuser, Garagen, Treppen – privat und gewerblich."
      path="/leistungen"
      breadcrumbs={crumbs}
      faq={faq}
      service="Winterdienst Leistungen Bremen"
      image={IMG.plowStreet}
    />
    <PageHero
      label="/ Leistungen"
      titleLines={["Unsere Leistungen –", "Winterdienst für jede", "Fläche in Bremen."]}
      intro="Von der Haustür bis zum Parkdeck: Jede Fläche hat eigene Anforderungen an Gerät, Streumittel und Zeitfenster. Hier finden Sie den Überblick – und auf jeder Unterseite die Details."
      image={IMG.plowStreet}
      breadcrumbs={crumbs}
    />


    <section className="bg-white py-16 lg:py-24">
      <div className="shell">
        <SectionHead
          label="/ Übersicht"
          title="Zwölf Leistungsbereiche – jeweils mit eigener Detailseite."
          text="Wählen Sie den Bereich, der zu Ihrem Objekt passt. Jede Seite beschreibt Problem, Lösung, Ablauf und geeignete Objekte."
        />
        <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICE_INDEX.map((s, i) => (
            <StaggerItem key={s.to}>
              <Link
                to={s.to}
                data-testid={`leistung-card${s.to.replace(/\//g, "-")}`}
                className="group flex h-full flex-col rounded-2xl border border-[#DCE5EF] bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-[#0757B8] hover:shadow-[0_34px_80px_-52px_rgba(6,43,82,0.6)]"
              >
                <span className="font-mono-label text-[#DCE5EF] transition-colors group-hover:text-[#FF6B00]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-6 text-[18px] font-bold tracking-tight group-hover:text-[#0757B8]">{s.title}</p>
                <p className="mt-3 flex-1 text-[13.5px] leading-relaxed text-[#66788A]">{s.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0757B8]">
                  Details ansehen
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={0.1}>
          <div className="mt-14 grid gap-4 rounded-2xl border border-[#DCE5EF] bg-[#F5F8FC] p-7 sm:grid-cols-2 lg:p-10">
            <div>
              <Label>/ Privat oder Gewerbe</Label>
              <h2 className="mt-4 text-[21px] font-bold sm:text-[26px]">Wofür brauchen Sie Winterdienst?</h2>
              <p className="mt-4 text-[14.5px] leading-relaxed text-[#66788A]">
                Die Anforderungen unterscheiden sich deutlich. Auf den beiden Hauptseiten
                finden Sie das jeweils passende Leistungsbild.
              </p>
            </div>
            <div className="flex flex-col justify-center gap-3">
              <Link to="/winterdienst-bremen-privat" data-testid="leistungen-privat" className="btn btn-dark h-13 py-3.5 text-sm">
                Winterdienst für Privatkunden <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/winterdienst-bremen-gewerbe-hausverwaltung" data-testid="leistungen-gewerbe" className="btn btn-orange h-13 py-3.5 text-sm">
                Winterdienst für Gewerbekunden <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FaqBlock items={faq} title="Fragen zu unseren Leistungen" />
    <CtaBand />
  </main>
);

export default Leistungen;
